import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { BlogPost } from '@/types/blog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { JsonLd } from '@/components/seo/JsonLd';
import { SITE_ORIGIN } from '@/lib/seo/site-url';
import { OpenGraph } from '@/components/seo/OpenGraph';
import { Helmet } from 'react-helmet-async';
import { CopyPostButton } from '@/components/blog/CopyPostButton';
import { SendToChatGPTButton } from '@/components/blog/SendToChatGPTButton';

interface BlogPostTraditionalProps {
  post: BlogPost;
}

/**
 * BlogPostTraditional - Renderização tradicional de post do blog
 * Usado para posts SEM vídeo sincronizado
 */
export function BlogPostTraditional({ post }: BlogPostTraditionalProps) {
  // Preparar dados para SEO
  const baseUrl = SITE_ORIGIN;
  const fullUrl = `${baseUrl}${post.url}`;
  const imageUrl = post.coverImage.startsWith('http')
    ? post.coverImage
    : `${baseUrl}${post.coverImage}`;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.seo?.title || post.title} | Johnny Vaz</title>
        <meta name="description" content={post.seo?.description || post.excerpt} />
        {post.seo?.keywords && (
          <meta name="keywords" content={post.seo.keywords.join(', ')} />
        )}
        <link rel="canonical" href={fullUrl} />
      </Helmet>

      {/* JSON-LD Schema */}
      <JsonLd post={post} baseUrl={baseUrl} />

      {/* Open Graph */}
      <OpenGraph
        title={post.seo?.title || post.title}
        description={post.seo?.description || post.excerpt}
        url={fullUrl}
        image={imageUrl}
        type="article"
        siteName="Johnny Vaz"
        locale={post.agentMetadata?.lang?.replace('-', '_') || 'pt_BR'}
        publishedTime={post.date.toISOString()}
        modifiedTime={post.lastModified?.toISOString()}
        author={post.schema?.author || 'Johnny Vaz'}
        section={post.categories[0]}
        tags={post.tags}
      />

      <article className="container max-w-4xl py-12">
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/blog" className="flex items-center group mb-4">
              <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Voltar para o blog
            </Link>
          </Button>

          <div className="flex items-start justify-between gap-4 mb-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold flex-1">{post.title}</h1>

            {/* AI Integration Buttons */}
            <div className="flex flex-col gap-2 shrink-0">
              <CopyPostButton post={post} />
              <SendToChatGPTButton post={post} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {post.categories.map((category, i) => (
              <Badge
                key={i}
                variant="secondary"
                className="bg-portfolio-primary/10 text-portfolio-primary hover:bg-portfolio-primary/20"
              >
                <Tag size={12} className="mr-1" /> {category}
              </Badge>
            ))}
          </div>

          <div className="flex items-center text-muted-foreground mb-8">
            <span className="flex items-center mr-4">
              <Calendar size={16} className="mr-1" /> {format(post.date, 'dd MMM yyyy', { locale: ptBR })}
            </span>
            <span className="flex items-center">
              <Clock size={16} className="mr-1" /> {post.readingTime} min de leitura
            </span>
          </div>

          {post.coverImage && (
            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}
        </div>

        <div
          className="prose dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />
      </article>
    </>
  );
}
